import styled from '@emotion/styled';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Grid, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import calendarBlue from 'assets/icons/calendar_blue.png';
import calendarGrey from 'assets/icons/calendar_grey.png';
import userBlue from 'assets/icons/user_blue.png';
import userGrey from 'assets/icons/user_grey.png';
import { CardOverview, ImageCarousel } from 'components';
import { CdATag } from 'components/TabsL2/TabsL2.style';
import {
  colors,
  CondizioniTransitabilitaL3Map,
  L2CdAMap,
} from 'constants/colors';
import { EffettivoPresunto } from 'constants/inspections';
import { IMAGE_CAROUSEL_MAX_IMAGES } from 'constants/media';
import {
  EsitoValutazioneL3Options,
  EvidenzeProfonditaDelleFondazioniOptions,
  ProtezioneAlPiedePileSpalleOptions,
} from 'constants/selectOptions';
import { inspectionTabMapping } from 'utils/inspectionTabMapping';
import { formatData } from 'utils/utilities';

// Contenuto condiviso del riepilogo L3, riusato da due consumer che
// chiamano ENTRAMBI GET bridges/:id/overview/L3 (2026-08-18 — decisione
// esplicita: niente più lettura dal form RHF live per L3Recap.jsx, stessa
// fonte dati del Dettaglio Ponte per i due riepiloghi):
// - L3Recap.jsx (sotto-tab "Riepilogo" dentro il form attività, passa
//   inspectionId esplicito — l'attività aperta stessa);
// - Structures/StructureDetail/Summary/TabL3/TabL3.jsx (tab "Idraulica" del
//   Dettaglio Ponte, inspectionId opzionale — se assente il BE usa l'ultima
//   ispezione valida per l'utente).
// flattenOverviewL3 (sotto) appiattisce BridgeL3OverviewDto (sezioni
// annidate informazioniGenerali/datiIdraulici/sormonto/erosione/
// cdaIdraulica/commentiReport) nella forma flat richiesta da questo
// componente — condivisa qui per evitare due copie divergenti.
//
// Issue #1021, punto 16 — redesign 2026-09-01 sul mockup cliente
// (plans/features/l3/LIVELLO 3 - Idraulica.pdf) e sullo stile grafico reale
// di TabL1.jsx/sections/{Ispezioni,Contesto}.jsx (screenshot cliente, non il
// mockup): ogni gruppo è un CardOverview grigio (titolo + "Approfondisci"),
// contenente sotto-card bianche (`subCard`) — stessa struttura di "Ispezioni"
// (3 sotto-card Ispezione Struttura/Frana/Idraulica) e "Contesto" (sotto-card
// Rischio idraulico/Rischio frane, ciascuna col proprio bottone). Sostituisce
// le vecchie sezioni "1. Informazioni generali" / "3-4. Sormonto ed Erosione".
// Carosello foto in cima, SENZA titolo (CardOverview height="200px", stesso
// pattern di TabL0.jsx/TabL1.jsx) — prima mancava nel sotto-tab "Riepilogo"
// in-attività (L3Recap.jsx), che non faceva questo fetch: ora lo fa, vedi
// commento in testa a quel file.
//
// makeApprofondisci(accordion)/approfondisciDisabled — un bottone per
// sezione, ciascuno verso il proprio accordion di AccordionDataL3 (indici in
// utils/inspectionTabMapping.js, costanti L3*), stesso pattern di
// TabL1/sections/Contesto.jsx (Rischio idraulico/Rischio frane hanno
// bottoni distinti dal padre "Contesto"): `makeApprofondisci` è una factory
// (accordion) => onClick, fornita dal chiamante (TabL3.jsx: verso l'ultima
// attività L3 valida; L3Recap.jsx: verso l'attività corrente, sempre valida
// essendo già aperta) — undefined per i gruppi eterogenei (Valutazione
// Idraulica, Sormonto ed Erosione) apre solo la sotto-tab senza forzare un
// accordion specifico, esattamente come "Contesto" in L1.
const NOT_AVAILABLE = 'n.d.';

const EffettivoPresuntoOptions = [
  { label: 'Effettivo', value: EffettivoPresunto.Effettivo },
  { label: 'Presunto', value: EffettivoPresunto.Presunto },
];

// Issue #1021, punto 16 — altezza fissa condivisa da Sormonto ed Erosione:
// col contenuto ad "auto" le due card avevano altezze diverse (Erosione ha
// più campi), il cliente vuole i due box della stessa dimensione. Valore
// scelto generoso a sufficienza per il ramo L2 di entrambi i fenomeni
// (badge + nota "derivata da L2" + fino a ~5 righe campo + vulnerabilità).
const PHENOMENON_CARD_HEIGHT = '250px';

// Stesso motivo di PHENOMENON_CARD_HEIGHT, per le 3 sotto-card del gruppo
// "Valutazione Idraulica" (Data e rilevatore/CdA Idraulica - L3/
// Transitabilità idraulica): Transitabilità è la più alta delle tre per via
// del testo descrittivo, ad "auto" le altre due restavano più basse.
const VALUTAZIONE_IDRAULICA_CARD_HEIGHT = '170px';

const labelFromOptions = (options, value) =>
  options.find(option => option.value === value)?.label;

const formatEnumValue = (options, value) => {
  if (value === undefined || value === null || value === '') {
    return NOT_AVAILABLE;
  }
  if (Array.isArray(value)) {
    if (!value.length) {
      return NOT_AVAILABLE;
    }
    return value.map(v => labelFromOptions(options, v) ?? v).join(', ');
  }
  return labelFromOptions(options, value) ?? String(value);
};

const formatText = value =>
  value === undefined || value === null || value === '' ? NOT_AVAILABLE : value;

const formatNumber = (value, unit = '') => {
  if (value === undefined || value === null || value === '') {
    return NOT_AVAILABLE;
  }
  const parsed = parseFloat(value);
  return isNaN(parsed) ? NOT_AVAILABLE : `${parsed}${unit}`;
};

const formatDate = value => {
  const formatted = value ? formatData(value) : undefined;
  return formatted || NOT_AVAILABLE;
};

// Coppia etichetta/valore, stesso pattern (Typography h3 + h3Bold annidata)
// già usato in TabL1/sections/Contesto.jsx. `fullWidth`: una riga per campo
// (xs=12 fisso, niente sm/md) — i breakpoint di MUI Grid sono relativi alla
// viewport, non al contenitore, quindi senza `fullWidth` i campi si
// dispongono su più colonne anche dentro una card di metà larghezza (es. le
// card Sormonto/Erosione affiancate); serve per il layout verticale
// richiesto per Erosione (issue #1021, punto 16).
const Field = ({ label, value, fullWidth }) => (
  <Grid item xs={12} sm={fullWidth ? 12 : 6} md={fullWidth ? 12 : 4}>
    <Typography variant="h3">
      {label} <Typography variant="h3Bold">{value}</Typography>
    </Typography>
  </Grid>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  fullWidth: PropTypes.bool,
};

// Stesso styled-div "Row" (display:flex, gap) di TabL1.styles.js/TabL0.styles.js
// — non condiviso da lì (pattern copiato per-file in tutto il progetto),
// replicato identico qui.
const Row = styled('div')(({ alignItems, gap = '12px' }) => ({
  display: 'flex',
  gap,
  alignItems,
}));

// Riga icona + label + valore, stesso pattern (icona PNG blu se il dato è
// presente, grigia altrimenti + Stack label/value) di
// TabL1/sections/Ispezioni.jsx (Data/Ispettore), qui come componente locale
// perché riusato due volte nella stessa mini-card.
const IconLabelValue = ({ activeIcon, inactiveIcon, active, label, value }) => (
  <Row alignItems="center">
    <img src={active ? activeIcon : inactiveIcon} alt="" />
    <Stack>
      <Typography variant="h3Bold">{label}</Typography>
      <Typography variant="h3">{value}</Typography>
    </Stack>
  </Row>
);

IconLabelValue.propTypes = {
  activeIcon: PropTypes.string.isRequired,
  inactiveIcon: PropTypes.string.isRequired,
  active: PropTypes.bool,
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

// Pillola con sfondo colorato — stesso registro grafico del badge classe CdA
// (CdaBadge/CdATag sotto), non il pallino del mockup originale: il cliente ha
// chiesto che anche "N.D." (nessun valore) resti visivamente una pillola con
// sfondo (grigio, come CdaBadge già fa per una classe CdA assente) invece di
// testo nudo — fallback esplicito quando `value` non è una delle 4 chiavi di
// CondizioniTransitabilitaL3Map (constants/colors.js).
// Stessa taglia/centratura di CdATag (components/TabsL2/TabsL2.style.js):
// width fisso (non "inline-block", che in uno Stack flex-column si allarga
// per lo stretch di default) + testo centrato, così la pillola "N.D." non
// risulta più larga delle altre (es. "ALTA").
const TransitabilitaPill = styled(Box)(({ labelColor }) => ({
  backgroundColor: labelColor,
  width: 140,
  textAlign: 'center',
  padding: 6,
  borderRadius: 12,
  marginBottom: 8,
}));

const TransitabilitaBadge = ({ value }) => {
  const entry = CondizioniTransitabilitaL3Map[value] ?? {
    labelColor: colors.SILVER,
    labelText: 'N.D.',
    description: '',
  };
  return (
    <Stack>
      <TransitabilitaPill labelColor={entry.labelColor}>
        <Typography variant="h3Bold">{entry.labelText}</Typography>
      </TransitabilitaPill>
      {entry.description && (
        <Typography variant="h5">{entry.description}</Typography>
      )}
    </Stack>
  );
};

TransitabilitaBadge.propTypes = {
  value: PropTypes.number,
};

// Riga di box per "Esito valutazione" (ID609) — stesso registro cromatico
// del PlanningBox già in uso nell'overview di L1
// (TabL1/sections/Contesto.jsx: bordo+sfondo bianco di default, blu quando
// il valore è quello atteso), ma SOLO titolo+colore: niente seconda riga di
// testo ("Selezionato"/"n.d.") — richiesto esplicitamente dal cliente, a
// differenza di PlanningBox che mostra sempre uno statusMessage (o "n.d."
// hardcoded quando `status` è assente). Componente locale minimale invece
// di riusare PlanningBox per questo motivo. Scelta multipla (RHFMultiSelect,
// Accordion5CdaL3.jsx, decisione PO 2026-09-17): il mockup PDF che mostra
// due pillole blu contemporaneamente era corretto, non un artefatto — vedi
// apps/api/docs/handoff-fe-task-20-box-overview-l3.md §7.
// Altezza fissa (stesso motivo di PHENOMENON_CARD_HEIGHT sopra): le 5
// etichette hanno lunghezze molto diverse ("Rapporto..." vs "Ulteriori
// ispezioni e approfondimenti speciali"), quindi vanno su 1 o 2 righe a
// seconda — senza un'altezza condivisa i 5 box non erano allineati.
const EsitoBox = styled(Box)(({ active }) => ({
  width: '100%',
  height: 64,
  display: 'flex',
  alignItems: 'center',
  borderRadius: 8,
  border: `1px solid ${active ? colors.BLUE : colors.PALAGREY}`,
  backgroundColor: active ? colors.BLUE : colors.WHITE,
  padding: 16,
  boxSizing: 'border-box',
}));

const EsitoValutazionePillRow = ({ value }) => (
  <Grid container spacing={1} marginTop={1}>
    {EsitoValutazioneL3Options.map(option => {
      const active = Array.isArray(value)
        ? value.includes(option.value)
        : option.value === value;
      return (
        <Grid item xs={12} sm={6} md={2.4} key={option.value}>
          <EsitoBox active={active}>
            <Typography
              variant="h3Bold"
              sx={{
                fontSize: '0.75rem',
                color: active ? colors.WHITE : colors.BLACK,
              }}
            >
              {option.label}
            </Typography>
          </EsitoBox>
        </Grid>
      );
    })}
  </Grid>
);

EsitoValutazionePillRow.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number),
  ]),
};

// Nota "CdA derivata dalla valutazione di Livello 2" — quando il fenomeno
// non è stato scelto in "Analisi L3 - Fenomeno analizzato" (577), il BE
// calcola la classe con l'algoritmo L2 tradizionale invece di L3 (stesso
// segnale calcMethod già usato in Accordion5CdaL3.jsx/issue #1021 punto
// 15a — vedi apps/api/docs/cda-idraulica-mista-l2-l3.md). Qui in versione
// compatta (una riga), non il banner InfoBox a piena larghezza della tab
// attività: lo spazio della mini-card è limitato.
const L2DerivedNote = () => (
  <Stack direction="row" alignItems="center" gap={0.5} mb={1}>
    <WarningAmberIcon sx={{ fontSize: 16, color: colors.WEBORANGE }} />
    <Typography variant="h5" sx={{ color: colors.WEBORANGE }}>
      CdA derivata dalla valutazione di Livello 2
    </Typography>
  </Stack>
);

// Badge classe CdA (Sormonto/Erosione/complessiva) — CdATag+L2CdAMap, stesso
// componente riusato ovunque nel progetto per le classi CdA (Accordion5CdaL3.jsx,
// TabsL2.jsx). cdaClass usa la numerazione client (L2CdAMap: 1=Bassa…5=Alta,
// 6=N.D.), NON quella di cdaIdraulica.cdaSormonto/cdaErosione/cdaIdraulica del
// DTO overview (numerazione BE ValoreCda, invertita) — per questo il valore
// va letto da cdaGraphicalRepresentation.{class,overlapCda.class,erosionCda.class},
// non dai campi scalari cdaSormonto/cdaErosione/cdaIdraulica.
const CdaBadge = ({ cdaClass }) => (
  <CdATag cda={cdaClass !== undefined} cdaSectionData={cdaClass}>
    <Typography variant="h3Bold">
      {L2CdAMap[cdaClass]?.labelText ?? 'N.D.'}
    </Typography>
  </CdATag>
);

CdaBadge.propTypes = {
  cdaClass: PropTypes.number,
};

const L3RecapContent = ({
  values,
  imagesList,
  imagesFallback,
  makeApprofondisci,
  approfondisciDisabled,
}) => {
  const cdaGraphicalRepresentation = values.cdaGraphicalRepresentation;
  const overallClass = cdaGraphicalRepresentation?.class;
  const overlapCda = cdaGraphicalRepresentation?.overlapCda;
  const erosionCda = cdaGraphicalRepresentation?.erosionCda;
  const sormontoIsL3 = overlapCda?.calcMethod === 'L3';
  const erosioneIsL3 = erosionCda?.calcMethod === 'L3';

  // "Approfondisci" per sezione, stesso pattern di TabL1/sections/Contesto.jsx
  // (Rischio idraulico/Rischio frane hanno bottoni distinti, l'accordion
  // padre "Contesto" no): `accordion` è l'indice 0-based in AccordionDataL3
  // (utils/inspectionTabMapping.js, costanti L3*) — `undefined` per i gruppi
  // eterogenei (Valutazione Idraulica, Sormonto ed Erosione), che aprono solo
  // la sotto-tab senza forzare un accordion specifico, esattamente come fa
  // "Contesto" in L1.
  const approfondisci = accordion => ({
    buttonText: 'Approfondisci',
    buttonDisabled: approfondisciDisabled,
    onClick: makeApprofondisci?.(accordion),
  });

  return (
    <>
      <CardOverview height="200px" buttonDisabled={true}>
        <div style={{ height: '90%', margin: 'auto 0' }}>
          <ImageCarousel
            items={imagesList?.length > 0 ? imagesList : imagesFallback}
            slidesPerView={IMAGE_CAROUSEL_MAX_IMAGES}
            canClick={imagesList?.length > 0}
            maxWidth={
              imagesList && imagesList?.length <= IMAGE_CAROUSEL_MAX_IMAGES
                ? '250px'
                : 'unset'
            }
          />
        </div>
      </CardOverview>

      <CardOverview
        title="Valutazione Idraulica"
        height="auto"
        {...approfondisci()}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <CardOverview
              subCard
              title="Data e rilevatore"
              height={VALUTAZIONE_IDRAULICA_CARD_HEIGHT}
              {...approfondisci(inspectionTabMapping.L3InformazioniGenerali)}
            >
              <Row gap="100px">
                <IconLabelValue
                  activeIcon={calendarBlue}
                  inactiveIcon={calendarGrey}
                  active={!!values.dataAttivita}
                  label="Data"
                  value={formatDate(values.dataAttivita)}
                />
                <IconLabelValue
                  activeIcon={userBlue}
                  inactiveIcon={userGrey}
                  active={!!values.rilevatore}
                  label="Rilevatore"
                  value={formatText(values.rilevatore)}
                />
              </Row>
            </CardOverview>
          </Grid>
          <Grid item xs={12} md={4}>
            <CardOverview
              subCard
              title="CdA Idraulica - L3"
              height={VALUTAZIONE_IDRAULICA_CARD_HEIGHT}
              {...approfondisci(inspectionTabMapping.L3CdaIdraulica)}
            >
              <CdaBadge cdaClass={overallClass} />
            </CardOverview>
          </Grid>
          <Grid item xs={12} md={4}>
            <CardOverview
              subCard
              title="Transitabilità idraulica"
              height={VALUTAZIONE_IDRAULICA_CARD_HEIGHT}
              {...approfondisci(inspectionTabMapping.L3CdaIdraulica)}
            >
              <TransitabilitaBadge value={values.condizioniTransitabilita} />
            </CardOverview>
          </Grid>
        </Grid>
      </CardOverview>

      <CardOverview
        title="Esito valutazione"
        height="auto"
        {...approfondisci(inspectionTabMapping.L3CdaIdraulica)}
      >
        <EsitoValutazionePillRow value={values.esitoDellaValutazione} />
      </CardOverview>

      <CardOverview
        title="Sormonto ed Erosione"
        height="auto"
        // {...approfondisci()}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CardOverview
              subCard
              title="Sormonto"
              height={PHENOMENON_CARD_HEIGHT}
              {...approfondisci(inspectionTabMapping.L3Sormonto)}
            >
              <Box mb={1.5}>
                <CdaBadge cdaClass={overlapCda?.class} />
              </Box>
              {!sormontoIsL3 && <L2DerivedNote />}
              <Grid container spacing={1}>
                <Field
                  fullWidth
                  label="Franco idraulico FP2 [m]"
                  value={formatNumber(values.francoIdraulicoFP2)}
                />
              </Grid>
            </CardOverview>
          </Grid>

          <Grid item xs={12} md={6}>
            <CardOverview
              subCard
              title="Erosione"
              height={PHENOMENON_CARD_HEIGHT}
              {...approfondisci(inspectionTabMapping.L3Erosione)}
            >
              <Box mb={1.5}>
                <CdaBadge cdaClass={erosionCda?.class} />
              </Box>
              {!erosioneIsL3 && <L2DerivedNote />}
              {/* Issue #1021, punto 16 — elenco e ordine dei 5 campi
              confermati dal cliente per QUESTA card di riepilogo (mockup
              PDF): diverso dai 6 campi del box Pericolosità nella tab
              attività (Accordion5CdaL3.jsx/erosioneL3DangerFields.js, dove
              612 compare da solo e 159b non compare affatto) — qui 159/159b
              e 171/172 sono accorpati su una riga ciascuno, 612 non compare,
              tutto disposto in colonna singola (`fullWidth`, non la griglia
              a più colonne di Field). */}
              <Grid container spacing={1}>
                <Field
                  fullWidth
                  label="Massima profondità di scavo ds totale [m]"
                  value={formatNumber(values.massimaProfonditaScavoTotale)}
                />
                <Field
                  fullWidth
                  label="Profondità fondazione df rispetto all'alveo [m]"
                  value={`${formatNumber(
                    values.profonditaFondazioneRispettoAlveo
                  )}${
                    values.profonditaFondazioneRispettoAlveoFlag !== undefined &&
                    values.profonditaFondazioneRispettoAlveoFlag !== null &&
                    values.profonditaFondazioneRispettoAlveoFlag !== ''
                      ? ` (${formatEnumValue(
                          EffettivoPresuntoOptions,
                          values.profonditaFondazioneRispettoAlveoFlag
                        )})`
                      : ''
                  }`}
                />
                <Field
                  fullWidth
                  label="Rapporto ds/df"
                  value={formatNumber(values.rapportoDsTotDf)}
                />
                <Field
                  fullWidth
                  label="Evidenze sulla profondità delle fondazioni"
                  value={formatEnumValue(
                    EvidenzeProfonditaDelleFondazioniOptions,
                    values.evidenzeProfonditaDelleFondazioni
                  )}
                />
                <Field
                  fullWidth
                  label="Protezione al piede delle pile e delle spalle del ponte"
                  value={`${formatEnumValue(
                    ProtezioneAlPiedePileSpalleOptions,
                    values.protezioneAlPiedePileSpalle
                  )} `}
                />
              </Grid>
            </CardOverview>
          </Grid>
        </Grid>
      </CardOverview>
    </>
  );
};

L3RecapContent.propTypes = {
  values: PropTypes.object.isRequired,
  imagesList: PropTypes.array,
  imagesFallback: PropTypes.array,
  makeApprofondisci: PropTypes.func,
  approfondisciDisabled: PropTypes.bool,
};

L3RecapContent.defaultProps = {
  approfondisciDisabled: true,
};

// Appiattisce la risposta di GET bridges/:id/overview/L3
// (BridgeL3OverviewDto, sezioni annidate) nella forma flat sopra — stessi
// nomi campo di Level3 lato form, per riusare identici formatter/Field.
const flattenOverviewL3 = overviewL3 => ({
  rilevatore: overviewL3?.informazioniGenerali?.rilevatore,
  dataAttivita: overviewL3?.informazioniGenerali?.dataAttivita,

  portataQP3: overviewL3?.datiIdraulici?.portataQP3,
  portataQP2: overviewL3?.datiIdraulici?.portataQP2,
  portataQP1: overviewL3?.datiIdraulici?.portataQP1,
  velocitaAMonteVP3: overviewL3?.datiIdraulici?.velocitaAMonteVP3,
  velocitaAMonteVP2: overviewL3?.datiIdraulici?.velocitaAMonteVP2,
  velocitaAMonteVP1: overviewL3?.datiIdraulici?.velocitaAMonteVP1,
  quotaPeloLiberoP3: overviewL3?.datiIdraulici?.quotaPeloLiberoP3,
  quotaPeloLiberoP2: overviewL3?.datiIdraulici?.quotaPeloLiberoP2,
  quotaPeloLiberoP1: overviewL3?.datiIdraulici?.quotaPeloLiberoP1,
  quotaMinimaFondoAlveo: overviewL3?.datiIdraulici?.quotaMinimaFondoAlveo,
  pendenzaMedia: overviewL3?.datiIdraulici?.pendenzaMedia,

  francoIdraulicoFP2: overviewL3?.sormonto?.francoIdraulicoFP2,

  evidenzeProfonditaDelleFondazioni:
    overviewL3?.erosione?.evidenzeProfonditaDelleFondazioni,
  massimaProfonditaScavoTotale:
    overviewL3?.erosione?.massimaProfonditaScavoTotale,
  profonditaFondazioneRispettoAlveo:
    overviewL3?.erosione?.profonditaFondazioneRispettoAlveo,
  profonditaFondazioneRispettoAlveoFlag:
    overviewL3?.erosione?.profonditaFondazioneRispettoAlveoFlag,
  rapportoDsTotDf: overviewL3?.erosione?.rapportoDsTotDf,
  protezioneAlPiedePileSpalle:
    overviewL3?.erosione?.protezioneAlPiedePileSpalle,
  statoConservazioneProtezionePiede:
    overviewL3?.erosione?.statoConservazioneProtezionePiede,

  esitoDellaValutazione: overviewL3?.cdaIdraulica?.esitoDellaValutazione,
  condizioniTransitabilita: overviewL3?.cdaIdraulica?.condizioniTransitabilita,
  // Badge classe complessiva/Sormonto/Erosione letti da
  // cdaGraphicalRepresentation (vedi sotto), non dai campi scalari
  // cdaSormonto/cdaErosione/cdaIdraulica del DTO (numerazione BE ValoreCda,
  // invertita rispetto a L2CdAMap) — quei tre scalari non servono più qui.
  // Issue #1021, punto 16 — usato per i badge classe Sormonto/Erosione/
  // complessiva (CdaBadge, numerazione client L2CdAMap) e per la nota "CdA
  // derivata da L2" (calcMethod): stesso oggetto già consumato in
  // Accordion5CdaL3.jsx, il BE lo passa "così com'è" nel DTO overview
  // (bridge-l3-overview.dto.ts, tipizzato `unknown` di proposito) — NON i
  // campi scalari cdaSormonto/cdaErosione/cdaIdraulica sopra, che usano la
  // numerazione BE ValoreCda (invertita rispetto a L2CdAMap).
  cdaGraphicalRepresentation:
    overviewL3?.cdaIdraulica?.cdaGraphicalRepresentation,

  inquadramentoTerritorialeDescrizione:
    overviewL3?.commentiReport?.inquadramentoTerritoriale?.descrizione,
  documentazioneDisponibileDescrizione:
    overviewL3?.commentiReport?.documentazioneDisponibile?.descrizione,
  descrizioneStrutturaDescrizione:
    overviewL3?.commentiReport?.descrizioneStruttura?.descrizione,
  caratterizzazioneSistemaDescrizione:
    overviewL3?.commentiReport?.caratterizzazioneSistema?.descrizione,
  scenariIdrauliciRiferimentoDescrizione:
    overviewL3?.commentiReport?.scenariIdrauliciRiferimento?.descrizione,
  valutazioneRischioSormontoFrancoIdraulico:
    overviewL3?.commentiReport?.valutazioneRischioSormonto?.descrizione,
  valutazioneFenomeniErosiviTesto:
    overviewL3?.commentiReport?.valutazioneFenomeniErosivi?.descrizione,
  conclusioniDescrizione: overviewL3?.commentiReport?.conclusioniDescrizione,
});

export { L3RecapContent, flattenOverviewL3 };
